import type { ReactNode } from 'react';

/**
 * Thin-line icon set, drawn for this theme (1.5 px strokes on a 24 px grid, so
 * they sit with the Outfit body weight). Always decorative: every use is paired
 * with visible text or an accessible name on the control, so the SVG carries
 * `aria-hidden`.
 */
export type IconName =
  | 'arrow-right'
  | 'arrow-left'
  | 'search'
  | 'heart'
  | 'user'
  | 'basket'
  | 'menu'
  | 'close'
  | 'ruler'
  | 'workshop'
  | 'shield'
  | 'truck'
  | 'leaf'
  | 'pencil'
  | 'layers'
  | 'quote'
  | 'grid'
  | 'chat'
  | 'pin'
  | 'tag'
  | 'swatch'
  | 'tools'
  | 'check'
  | 'chevron-down'
  | 'phone';

const PATHS: Record<IconName, ReactNode> = {
  'arrow-right': <path d="M4 12h15m-5-5 5 5-5 5" />,
  'arrow-left': <path d="M20 12H5m5-5-5 5 5 5" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5" />
    </>
  ),
  heart: (
    <path d="M12 20s-7.5-4.6-7.5-10.1A4.4 4.4 0 0 1 12 7.1a4.4 4.4 0 0 1 7.5 2.8C19.5 15.4 12 20 12 20Z" />
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.75" />
      <path d="M4.5 20.25c.9-3.6 3.9-5.5 7.5-5.5s6.6 1.9 7.5 5.5" />
    </>
  ),
  basket: (
    <>
      <path d="M3.5 9h17l-1.6 10.1a1.5 1.5 0 0 1-1.5 1.3H6.6a1.5 1.5 0 0 1-1.5-1.3L3.5 9Z" />
      <path d="M8.5 9 12 3.75 15.5 9" />
    </>
  ),
  menu: <path d="M3.75 7h16.5M3.75 12h16.5M3.75 17h16.5" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  ruler: (
    <>
      <rect x="2.75" y="8" width="18.5" height="8" rx="1" />
      <path d="M6.5 8v3M10 8v4.5M13.5 8v3M17 8v4.5" />
    </>
  ),
  workshop: (
    <>
      <path d="M3 20.25V9.5l5 3v-3l5 3v-3l8 4.75v6" />
      <path d="M2 20.25h20M8 16.5h2.5M13.5 16.5H16" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.25 19.25 6v5.4c0 4.6-3 7.9-7.25 9.35C7.75 19.3 4.75 16 4.75 11.4V6L12 3.25Z" />
      <path d="m8.75 12 2.25 2.25 4.25-4.5" />
    </>
  ),
  truck: (
    <>
      <path d="M2.75 6.25h11v10h-11zM13.75 9.75h4.1l3.4 3.4v3.1h-7.5" />
      <circle cx="7" cy="17.75" r="1.75" />
      <circle cx="17.25" cy="17.75" r="1.75" />
    </>
  ),
  leaf: (
    <>
      <path d="M19.5 4.5c-8.5 0-14.25 4.1-14.25 10.6 0 2.3.9 3.9.9 3.9s1.6.75 3.6.75c6.25 0 9.75-6 9.75-15.25Z" />
      <path d="M4.5 20c3.25-4.75 6.25-7.5 10-9.5" />
    </>
  ),
  pencil: (
    <>
      <path d="m15.25 4.75 4 4L8.5 19.5l-4.75.75.75-4.75L15.25 4.75Z" />
      <path d="m13 7 4 4" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3.75 8.75 4.5L12 12.75l-8.75-4.5L12 3.75Z" />
      <path d="m3.25 12.25 8.75 4.5 8.75-4.5M3.25 16.25l8.75 4.5 8.75-4.5" />
    </>
  ),
  quote: (
    <path d="M9.75 7.25c-3 .8-4.5 3-4.5 6.25v3.25h4.5v-4.5h-2.5c0-1.9.9-3 2.5-3.5V7.25Zm9 0c-3 .8-4.5 3-4.5 6.25v3.25h4.5v-4.5h-2.5c0-1.9.9-3 2.5-3.5V7.25Z" />
  ),
  grid: (
    <>
      <rect x="3.75" y="3.75" width="16.5" height="16.5" rx="1" />
      <path d="M3.75 9.25h16.5M3.75 14.75h16.5M9.25 3.75v16.5M14.75 3.75v16.5" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5.75h16v10H11l-4.5 3.5v-3.5H4v-10Z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 20.75s-6.75-6-6.75-11.5a6.75 6.75 0 0 1 13.5 0c0 5.5-6.75 11.5-6.75 11.5Z" />
      <circle cx="12" cy="9.25" r="2.5" />
    </>
  ),
  tag: (
    <>
      <path d="M3.75 12V3.75H12l8.25 8.25L12 20.25 3.75 12Z" />
      <circle cx="8" cy="8" r="1.25" />
    </>
  ),
  swatch: (
    <>
      <path d="M4 3.75h6v16.5H4zM10 8l5-3 4.25 7.25-9.25 6" />
      <circle cx="7" cy="16.5" r=".75" />
    </>
  ),
  tools: (
    <path d="M14.5 6.25a4 4 0 0 0-5.35 5.35L3.25 17.5l3.25 3.25 5.9-5.9a4 4 0 0 0 5.35-5.35l-2.5 2.5-2.4-.6-.6-2.4 2.25-2.75Z" />
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  'chevron-down': <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />,
  phone: (
    <path d="M5.25 3.75h3.5l1.75 4.5-2.25 1.5a11 11 0 0 0 6 6l1.5-2.25 4.5 1.75v3.5a1.5 1.5 0 0 1-1.5 1.5C10.5 20.25 3.75 13.5 3.75 5.25a1.5 1.5 0 0 1 1.5-1.5Z" />
  ),
};

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export function isIconName(value: unknown): value is IconName {
  return typeof value === 'string' && value in PATHS;
}

export function Icon({ name, size = 20, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
